import { reactive, toRaw } from 'vue'
import type { StudentAggStats, GroupAggStats, StudentStats } from '@/store/stats/types'

export type Loadable<T> = {
    isLoading: boolean
    lastFetched: null | Date
    value: T | Promise<T>
}

export type LoadableProps<T> = {
    [ K in Extract<keyof T, string> ]: T[K] extends Loadable<unknown> | Record<string, Loadable<unknown>> ? K : never
}[Extract<keyof T, string>]

export const initLoadable = <T>(value: T): Loadable<T> => ({
    isLoading: false,
    lastFetched: null,
    value,
})

export const resetLoadable = <T, L extends Loadable<T>>(loadable: L | undefined) => {
    if (loadable) {
        loadable.lastFetched = null
    }
}

export const fetchLibLoadable = async <
    Module extends {
        state: {
            [stateKey in StateKey]: {
                [dictionaryId in DictionaryKey]: Loadable<LoadableValue> | undefined
            }
        }
    },
    LoadableValue,
    StateKey extends keyof Module['state'],
    DictionaryKey extends keyof Module['state'][StateKey]
>(
    module: Module,
    stateKey: StateKey,
    dictionaryKey: DictionaryKey,
    asyncFunc: () => Promise<LoadableValue>,
    forceFetch?: boolean
): Promise<LoadableValue> => {
    let loadable = reactive(module.state[stateKey][dictionaryKey] || initLoadable({})) as Loadable<LoadableValue>

    // if loadable value is already a promise or if a resolved value has already been stored, return it
    if (!forceFetch && (loadable?.isLoading || loadable?.lastFetched)) {
        return loadable.value
    }

    if (!loadable) {
        // if you are here, this is the first time this function is being called with the specified dictionary ID
        loadable = initLoadable({}) as Loadable<LoadableValue>

        const newState = {
            ...module.state[stateKey],
            [dictionaryKey]: loadable,
        }

        module.state[stateKey] = toRaw(reactive(newState) as typeof newState)
    }

    const promise = asyncFunc()
    loadable.isLoading = true
    // if we are force fetching, we want to keep original value in store until new value is loaded
    // (don't reset loadable value)
    if (!forceFetch) {
        loadable.value = promise

        module.state[stateKey][dictionaryKey] = loadable
    }

    const result = await promise
    // Only store the final result if isLoading === true
    // This lets us "cancel" in-flight requests by setting isLoading = false
    if (loadable?.isLoading) {
        loadable.isLoading = false
        loadable.lastFetched = new Date()
        loadable.value = result

        module.state[stateKey][dictionaryKey] = loadable
    }
    return result
}

export const fetchLoadable = async <T>(
    loadable: Loadable<T>,
    asyncFunc: () => Promise<T>,
    forceFetch?: boolean
): Promise<T> => {
    if (!forceFetch && (loadable.isLoading || loadable.lastFetched)) {
        return loadable.value
    }

    const promise = asyncFunc()
    loadable.isLoading = true
    if (!forceFetch) {
        loadable.value = promise
    }

    const result = await promise
    // Only store the final result if isLoading === true
    // This lets us "cancel" in-flight requests by setting isLoading = false
    if (loadable?.isLoading || forceFetch) {
        loadable.isLoading = false
        loadable.lastFetched = new Date()
        loadable.value = result
    }
    return result
}

export const fetchBatchLoadable = async <
    Module extends {
        state: {
            [stateKey in StateKey]: {
                [dictionaryId in DictionaryKey]: Loadable<LoadableValue | null> | undefined
            }
        }
    },
    StateKey extends keyof Module['state'],
    DictionaryKey extends keyof Module['state'][StateKey],
    LoadableValue
>(
    module: Module,
    stateKey: StateKey,
    dictionaryKeys: DictionaryKey[], // These are orgStudentIds
    asyncFunc: (filteredKeys: DictionaryKey[]) => Promise<Partial<Record<DictionaryKey, LoadableValue>>>,
    forceFetch?: boolean
): Promise<Partial<Record<DictionaryKey, LoadableValue>>> => {
    // Keys to be passed to the callback to resolve (i.e. what we actually want to fetch)
    const filteredKeys: DictionaryKey[] = []

    dictionaryKeys.forEach(dictionaryKey => {
        let loadable = module.state[stateKey][dictionaryKey]
        if (!loadable || !loadable.lastFetched || forceFetch) {
            // If we are here, then the given dictionaryKey has no entry in the store state
            // We don't have this key in store state - want to pass it to the callback
            filteredKeys.push(dictionaryKey)

            loadable = initLoadable(null)

            const newState = {
                ...module.state[stateKey],
                [dictionaryKey]: loadable,
            }

            module.state[stateKey] = reactive(newState) as typeof newState
        }
    })

    // At this point, we should call the callback with the remaining keys
    if (filteredKeys.length) {
        const promise = asyncFunc(filteredKeys)

        filteredKeys.forEach(filteredKey => {
            const loadable = module.state[stateKey][filteredKey]
            if (loadable) {
                // Loadable should exist as we created them above
                loadable.isLoading = true

                if (!forceFetch) {
                    // We want each key's loadable to resolve to the key's value
                    loadable.value = promise.then(result => result[filteredKey] || null)
                    module.state[stateKey][filteredKey] = loadable
                }
            }
        })

        const loadableBatchResult = await promise

        for (const filteredKey in loadableBatchResult) {
            const result = loadableBatchResult[filteredKey]
            const loadable = module.state[stateKey][filteredKey]

            if (loadable?.isLoading) {
                loadable.isLoading = false
                loadable.lastFetched = new Date()
                loadable.value = result || null
                module.state[stateKey][filteredKey] = loadable
            }
        }

        return loadableBatchResult
    }

    return {}
}

export const LoadingStudentAggStats: StudentAggStats = {
    isLoading: true,
    correctAnswerCount: 100,
    totalAnswers: 130,
    totalQuestions: 600,
    subjectInsights: [],
    flaggedQuestionSerials: [],
    correctAnswerSerials: [],
    incorrectAnswerSerials: [],
    questionAnswerLib: {},
}

export const LoadingStudentStats: StudentStats = {
    answeredQuestions: [],
    isLoading: true,
    secondsStudied: 0,
    userInfo: {
        firstName: '',
        lastName: '',
        scheduledExamDate: undefined,
    },
    dayScores: [],
    quizScores: [],
    allActivity: [],
    qotdActivity: [],
    datesActive: [],
    flaggedQuestions: [],
}

export const LoadingGroupAggStats: GroupAggStats = {
    isLoading: true,
    overallScorePct: 0,
    answeredCount: 0,
    totalCount: 0,
    subjectInsights: [],
    questionStats: [],
}
